// File: frontend/src/components/ui/emptyStates.js
//
// §13.3 — the four empty states, and why they may never look the same.
//
// THE POINT OF THIS FILE
//   Ten phases of this programme went into making the backend tell the
//   difference between "we measured, and the answer is zero" and "we could not
//   measure". §2.1 exists for it, every `readMetric` in the rebuilt surfaces
//   enforces it, and the payload carries `available: false` with a reason
//   precisely so that a figure nobody could compute never renders as a 0.
//
//   All of that is thrown away at the last step if the UI renders both as the
//   same grey box saying "No data". This file is the last mile: it makes the
//   four cases produce four different sentences, four different icons and four
//   different next actions, from one place, so no surface can quietly collapse
//   them again.
//
// THE FOUR CASES
//
//   FIRST_RUN     This tenant has never had one of these. The list is empty and
//                 it is CORRECT that it is empty. The next action is to create
//                 the first one. Saying "no results" here is wrong — there is
//                 no filter to widen and no period to change; there is a thing
//                 that does not exist yet.
//
//   NO_ACTIVITY   The query succeeded, the scope is real, and this PERIOD has
//                 nothing in it. A quiet Sunday is a fact about the business,
//                 not a fault. The next action is to widen the period — never
//                 "try again", which implies something failed.
//
//   FILTERED      The data exists; the current filters exclude all of it. The
//                 next action is to clear or widen a filter, and the state must
//                 say WHICH filters are in force, because the commonest cause
//                 of this screen is a filter the reader forgot they set (or one
//                 that arrived in a deep link they followed).
//
//   UNAVAILABLE   Nobody knows whether it is empty. The request failed, or the
//                 server answered `available: false`, or a capability is not
//                 present. The next action is to retry or to escalate, and the
//                 state must say IN WORDS that an absence of rows here is not
//                 evidence of an absence of records. This is the case that must
//                 never be allowed to read like the other three.
//
// WHAT THIS FILE DOES NOT DO
//   It does not decide WHICH case applies. It cannot: only the caller knows
//   whether a filter is set, whether the tenant is new, and whether the server
//   said it could not measure. It supplies the wording, the icon and the tone
//   once the caller has decided, so that thirty call sites cannot invent thirty
//   phrasings of the same four ideas.

import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

/** The taxonomy. Use the constants — a typo'd string silently falls back. */
export const EMPTY_KIND = {
  firstRun: "first-run",
  noActivity: "no-activity",
  filtered: "filtered",
  unavailable: "unavailable",
  error: "error",
  // The reader may not see this. Not "there is none" — "you may not look".
  noPermission: "no-permission",
  // The product does not do this yet. Not a fault and not an empty result.
  notImplemented: "not-implemented",
};

export const EMPTY_KINDS = Object.values(EMPTY_KIND);

// Tone drives the accent colour AND, deliberately, is never the only signal:
// each kind also has its own icon and its own sentence, so the four remain
// distinguishable to a reader who cannot see the colour at all (§13.4, "no
// colour-only encoding").
const SPEC = {
  [EMPTY_KIND.firstRun]: {
    tone: "primary",
    icon: AddCircleOutlineOutlinedIcon,
    title: (s) => `No ${s} yet`,
    body: (s) => `Nothing has been created here yet. This is the expected state for a new setup — it is not a fault and nothing is missing.`,
    hint: (s) => `Create the first ${singular(s)} to get started.`,
  },

  [EMPTY_KIND.noActivity]: {
    tone: "neutral",
    icon: EventBusyOutlinedIcon,
    title: (s) => `No ${s} in this period`,
    body: () => "The data loaded successfully and this period genuinely contains none. A quiet period is a real result, not a failure to read.",
    hint: () => "Widen the date range to see more.",
  },

  [EMPTY_KIND.filtered]: {
    tone: "info",
    icon: FilterAltOffOutlinedIcon,
    title: (s) => `No ${s} match these filters`,
    body: () => "The data loaded successfully; nothing in it matches the filters currently in force.",
    hint: () => "Clear a filter or widen the date range to see more.",
  },

  [EMPTY_KIND.unavailable]: {
    tone: "warning",
    icon: CloudOffOutlinedIcon,
    title: () => "Temporarily unavailable",
    body: (s) => `This could not be measured, so nothing is shown. An empty list here is NOT evidence that there are no ${s}.`,
    hint: () => "Try again in a moment. If it persists, the figure is unavailable rather than zero.",
  },

  [EMPTY_KIND.error]: {
    tone: "error",
    icon: ErrorOutlineOutlinedIcon,
    title: (s) => `${capitalise(s)} could not be loaded`,
    body: (s) => `The request failed, so nothing is shown. This is not evidence that there are no ${s}.`,
    hint: () => "Retry, or check the connection and try again.",
  },

  [EMPTY_KIND.noPermission]: {
    tone: "neutral",
    icon: LockOutlinedIcon,
    title: (s) => `You do not have access to ${s}`,
    body: (s) => `Your role does not include ${s}, so nothing is shown. This is not evidence that there are none.`,
    hint: () => "Ask the business owner if you need it.",
  },

  [EMPTY_KIND.notImplemented]: {
    tone: "neutral",
    icon: ConstructionOutlinedIcon,
    title: () => "Not available yet",
    body: () => "PesaFii does not do this yet. Nothing has failed and nothing is missing.",
    hint: () => null,
  },
};

const FALLBACK = {
  tone: "neutral",
  icon: Inventory2OutlinedIcon,
  title: (s) => `No ${s}`,
  body: () => null,
  hint: () => null,
};

function capitalise(s) {
  const t = String(s || "").trim();
  return t ? t[0].toUpperCase() + t.slice(1) : t;
}

// Good enough for the nouns this app actually uses — "packages", "payments",
// "transactions", "vouchers". Not a general inflector, and not pretending to be.
function singular(s) {
  const t = String(s || "item").trim();
  if (/ies$/i.test(t)) return `${t.slice(0, -3)}y`;
  if (/ses$/i.test(t)) return t.slice(0, -2);
  if (/s$/i.test(t)) return t.slice(0, -1);
  return t;
}

/**
 * Resolve one of the four cases into everything a surface needs to render it.
 *
 * @param {object}  opts
 * @param {string}  opts.kind      one of EMPTY_KIND (unknown → neutral fallback)
 * @param {string}  opts.subject   the plural noun: "packages", "payments"
 * @param {string}  [opts.title]   caller override — wins over the default
 * @param {string}  [opts.body]    caller override
 * @param {string}  [opts.hint]    caller override; appended after the body
 * @param {string}  [opts.reason]  the SERVER's own words, when it gave any.
 *                                 Rendered after the body, never instead of it:
 *                                 "column does not exist" is a cause, not an
 *                                 explanation, and the reader needs both.
 * @param {string}  [opts.filterSummary]  for `filtered` — which filters are on.
 *
 * @returns {{kind,tone,icon,title,body,hint,reason,description}}
 *   `description` is body + reason + hint joined into one paragraph, for the
 *   surfaces that render a single block of prose.
 */
export function resolveEmptyState({
  kind,
  subject = "records",
  title,
  body,
  hint,
  reason,
  filterSummary,
} = {}) {
  const spec = SPEC[kind] || FALLBACK;

  const resolvedTitle = title || spec.title(subject);
  let resolvedBody = body || spec.body(subject) || null;

  // The filtered case is the one where naming the cause changes the outcome:
  // a reader who cannot see which filters are in force cannot tell a deliberate
  // narrow view from a link that over-filtered.
  if (kind === EMPTY_KIND.filtered && filterSummary && !body) {
    resolvedBody = `${resolvedBody} In force: ${filterSummary}.`;
  }

  const resolvedHint = hint !== undefined ? hint : spec.hint(subject);

  const description = [resolvedBody, reason || null, resolvedHint]
    .filter(Boolean)
    .join(" ");

  return {
    kind: kind || "unknown",
    tone: spec.tone,
    icon: spec.icon,
    title: resolvedTitle,
    body: resolvedBody,
    hint: resolvedHint,
    reason: reason || null,
    description,
  };
}

/**
 * The commonest decision, made once so every list makes it the same way.
 *
 * Note what it does NOT do: it never returns `firstRun` on its own initiative.
 * "This tenant has never had one" is a claim about history that no component
 * rendering one page of results can make, and guessing it produces "No packages
 * yet — create your first" on a page whose filter simply excluded all forty.
 * The caller passes `everHadAny: false` only when it actually knows.
 */
export function classifyEmpty({
  forbidden = false,
  error = null,
  unavailable = false,
  filtersActive = false,
  everHadAny = null,
} = {}) {
  if (forbidden) return EMPTY_KIND.noPermission;
  if (error) return EMPTY_KIND.error;
  if (unavailable) return EMPTY_KIND.unavailable;
  if (filtersActive) return EMPTY_KIND.filtered;
  if (everHadAny === false) return EMPTY_KIND.firstRun;
  return EMPTY_KIND.noActivity;
}
